import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangleIcon } from 'lucide-react';
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut'
    }
  }
};
export function ProblemSection() {
  return <section className="px-4 py-20 md:py-28" data-id="element-146">
      <div className="max-w-3xl mx-auto" data-id="element-147">
        {/* The Problem */}
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.5,
        ease: 'easeOut'
      }} data-id="element-148">
          <div className="border-t-2 border-rescue-gold/40 w-16 mb-6" aria-hidden="true" data-id="element-149" />
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-8" data-id="element-150">
            The Problem
          </h2>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.5,
        delay: 0.1,
        ease: 'easeOut'
      }} className="space-y-5 mb-16" data-id="element-151">
          <p className="font-body text-base md:text-lg text-gray-300 leading-relaxed" data-id="element-152">
            You aren't "bad at money." You are caught in a system{' '}
            <span className="text-rescue-gold font-semibold" data-id="element-153">
              designed to confuse you
            </span>{' '}
            into paying for "features" you don't need. The tax prep industry
            spends millions to hide the free government tools you are legally
            entitled to use.
          </p>
          <p className="font-body text-base md:text-lg text-gray-300 leading-relaxed" data-id="element-154">
            They want you to believe filing your own taxes is a dark art that
            requires a $400-an-hour accountant or a bloated software giant. They
            use{' '}
            <span className="text-rescue-gold font-semibold" data-id="element-155">
              "Audit Protection"
            </span>{' '}
            as a scare tactic to sell you peace of mind that should already be
            yours for free. If you don't have the cash to pay a corporation to
            tell the government how much money you made, you feel like a
            failure.
          </p>
        </motion.div>

        {/* Why What You Tried Didn't Work */}
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.5,
        ease: 'easeOut'
      }} data-id="element-156">
          <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-8" data-id="element-157">
            Why What You Tried Didn't Work
          </h3>
        </motion.div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-40px'
      }} className="space-y-4" data-id="element-158">
          <motion.div variants={itemVariants} className="bg-midnight-800 border border-midnight-700/60 rounded-lg p-6" data-id="element-159">
            <div className="flex items-start gap-4" data-id="element-160">
              <div className="flex-shrink-0 mt-0.5" data-id="element-161">
                <AlertTriangleIcon className="w-5 h-5 text-rescue-red" aria-hidden="true" data-id="element-162" />
              </div>
              <div data-id="element-163">
                <h4 className="font-body text-lg font-bold text-white mb-2" data-id="element-164">
                  The "Free Edition" Trap
                </h4>
                <p className="font-body text-gray-400 leading-relaxed" data-id="element-165">
                  It's only free until you add a single 1099 or a basic
                  deduction, then the price jumps to $60, $90, or $120
                  instantly.
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div variants={itemVariants} className="bg-midnight-800 border border-midnight-700/60 rounded-lg p-6" data-id="element-166">
            <div className="flex items-start gap-4" data-id="element-167">
              <div className="flex-shrink-0 mt-0.5" data-id="element-168">
                <AlertTriangleIcon className="w-5 h-5 text-rescue-red" aria-hidden="true" data-id="element-169" />
              </div>
              <div data-id="element-170">
                <h4 className="font-body text-lg font-bold text-white mb-2" data-id="element-171">
                  Artificial Complexity
                </h4>
                <p className="font-body text-gray-400 leading-relaxed" data-id="element-172">
                  They use jargon to make simple questions feel like trap doors,
                  forcing you to pay for "expert help."
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div variants={itemVariants} className="bg-midnight-800 border border-midnight-700/60 rounded-lg p-6" data-id="element-173">
            <div className="flex items-start gap-4" data-id="element-174">
              <div className="flex-shrink-0 mt-0.5" data-id="element-175">
                <AlertTriangleIcon className="w-5 h-5 text-rescue-red" aria-hidden="true" data-id="element-176" />
              </div>
              <div data-id="element-177">
                <h4 className="font-body text-lg font-bold text-white mb-2" data-id="element-178">
                  The Sunk Cost Shakedown
                </h4>
                <p className="font-body text-gray-400 leading-relaxed" data-id="element-179">
                  They wait until the deadline is hours away to reveal the
                  price, knowing you're too tired to start over elsewhere.
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div variants={itemVariants} className="bg-midnight-800 border border-midnight-700/60 rounded-lg p-6" data-id="element-180">
            <div className="flex items-start gap-4" data-id="element-181">
              <div className="flex-shrink-0 mt-0.5" data-id="element-182">
                <AlertTriangleIcon className="w-5 h-5 text-rescue-red" aria-hidden="true" data-id="element-183" />
              </div>
              <div data-id="element-184">
                <h4 className="font-body text-lg font-bold text-white mb-2" data-id="element-185">
                  Search Engine Sabotage
                </h4>
                <p className="font-body text-gray-400 leading-relaxed" data-id="element-186">
                  When you Google "Free File," the first four results are paid
                  ads disguised as government portals.
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}